import { useState, useEffect, useRef } from 'react';

interface AdaptiveTextConfig {
  maxWidthAtLargeScreen: number; // максимальная ширина текста при большом экране
  maxWidthAtSmallScreen: number; // максимальная ширина текста при маленьком экране
  largeScreenWidth: number;      // ширина большого экрана
  smallScreenWidth: number;      // ширина маленького экрана
}

export const useAdaptiveText = ({
  maxWidthAtLargeScreen = 200,
  maxWidthAtSmallScreen = 150,
  largeScreenWidth = 500,
  smallScreenWidth = 320
}: AdaptiveTextConfig) => {
  const measureRef = useRef<HTMLSpanElement | null>(null);
  const [currentMaxWidth, setCurrentMaxWidth] = useState(maxWidthAtLargeScreen);

  useEffect(() => {
    const calculateMaxWidth = () => {
      const viewportWidth = window.innerWidth;

      // Если экран шире largeScreenWidth, используем максимальную ширину
      if (viewportWidth >= largeScreenWidth) {
        setCurrentMaxWidth(maxWidthAtLargeScreen);
        return;
      }

      // Если экран уже smallScreenWidth, используем минимальную ширину
      if (viewportWidth <= smallScreenWidth) {
        setCurrentMaxWidth(maxWidthAtSmallScreen);
        return;
      }

      // Иначе вычисляем пропорционально
      const ratio = (viewportWidth - smallScreenWidth) / (largeScreenWidth - smallScreenWidth);
      const width = maxWidthAtSmallScreen + (maxWidthAtLargeScreen - maxWidthAtSmallScreen) * ratio;
      setCurrentMaxWidth(Math.round(width));
    };

    calculateMaxWidth();

    const resizeObserver = new ResizeObserver(() => {
      calculateMaxWidth();
    });

    resizeObserver.observe(document.body);

    return () => {
      resizeObserver.disconnect();
    };
  }, [maxWidthAtLargeScreen, maxWidthAtSmallScreen, largeScreenWidth, smallScreenWidth]);

  const truncateText = (text: string): string => {
    if (!text || !measureRef.current) return text;

    const testSpan = measureRef.current;
    testSpan.style.visibility = 'hidden';
    testSpan.style.position = 'absolute';
    testSpan.style.whiteSpace = 'nowrap';
    testSpan.textContent = text;

    if (testSpan.offsetWidth <= currentMaxWidth) {
      return text;
    }

    let left = 0;
    let right = text.length;
    let result = text;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const truncated = `${text.slice(0, mid)}...`;
      testSpan.textContent = truncated;

      if (testSpan.offsetWidth <= currentMaxWidth) {
        result = truncated;
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }

    return result;
  };

  return { truncateText, measureRef };
}; 